import api from './api';
import localStorageService from './LocalStorageService';

class SyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.syncInterval = null;
    this.retryDelay = 5000; // 5 secondes
    this.maxRetries = 3;
    
    this.setupEventListeners();
    this.startPeriodicSync();
  }

  setupEventListeners() {
    // Écouter les changements de statut de connexion
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.onConnectionRestored();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.onConnectionLost();
    });
  }

  startPeriodicSync() {
    // Synchronisation automatique toutes les 30 secondes si en ligne
    this.syncInterval = setInterval(() => {
      if (this.isOnline && !this.syncInProgress) {
        this.syncPendingChanges();
      }
    }, 30000);
  }

  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async onConnectionRestored() {
    console.log('Connexion restaurée - Début de la synchronisation');
    await this.updateSyncStatus({ isOnline: true, status: 'syncing' });
    
    // Synchroniser immédiatement
    await this.syncPendingChanges();
  }

  async onConnectionLost() {
    console.log('Connexion perdue - Mode hors ligne activé');
    await this.updateSyncStatus({ isOnline: false, status: 'offline' });
  }

  async syncPendingChanges() {
    if (this.syncInProgress || !this.isOnline) {
      return;
    }

    this.syncInProgress = true;
    console.log('🔄 Début de la synchronisation simplifiée...');
    
    try {
      await this.updateSyncStatus({ isOnline: true, status: 'syncing' });
      
      const queue = await localStorageService.getSyncQueue();
      const pendingItems = queue.filter(item => item.status === 'pending');
      
      if (pendingItems.length === 0) {
        console.log('✅ Aucun élément à synchroniser');
        await this.updateSyncStatus({ 
          isOnline: true, 
          status: 'synced', 
          lastSync: new Date().toISOString(),
          pendingItems: 0
        });
        return;
      }

      console.log(`📦 ${pendingItems.length} éléments à synchroniser`);

      // ✅ NOUVELLE APPROCHE SIMPLIFIÉE : Boucle séquentielle
      for (let i = 0; i < pendingItems.length; i++) {
        const item = pendingItems[i];
        console.log(`🔄 Synchronisation ${i + 1}/${pendingItems.length}: ${item.type} ${item.operation}`);
        
        try {
          // 1. Vérifier si l'élément existe déjà en base (pour éviter les doublons)
          const exists = await this.checkIfExistsInDatabase(item);
          if (exists) {
            console.log(`⚠️ ${item.type} ${item.data.id} existe déjà en base, suppression locale`);
            await localStorageService.deleteEntity(item.type, item.data.id);
            await localStorageService.removeFromSyncQueue(item.id);
            continue;
          }

          // 2. Envoyer l'élément au serveur
          const result = await this.syncItem(item);
          
          if (result.success) {
            // 3. Supprimer immédiatement de la base locale
            await localStorageService.deleteEntity(item.type, item.data.id);
            await localStorageService.removeFromSyncQueue(item.id);
            console.log(`✅ ${item.type} ${item.data.id} synchronisé et supprimé localement`);
          } else {
            console.error(`❌ Échec synchronisation ${item.type} ${item.data.id}:`, result.error);
          }
          
        } catch (error) {
          console.error(`❌ Erreur synchronisation ${item.type} ${item.data.id}:`, error);
          // Continue avec l'élément suivant même en cas d'erreur
        }
        
        // Mettre à jour le statut après chaque élément
        const remainingItems = await localStorageService.getSyncQueue();
        await this.updateSyncStatus({ 
          isOnline: true, 
          status: 'syncing',
          pendingItems: remainingItems ? remainingItems.length : 0 
        });
      }

      console.log('✅ Synchronisation terminée avec succès');
      await this.updateSyncStatus({ 
        isOnline: true, 
        status: 'synced', 
        lastSync: new Date().toISOString(),
        pendingItems: 0
      });

    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      await this.updateSyncStatus({ 
        isOnline: true, 
        status: 'error', 
        error: error.message 
      });
    } finally {
      this.syncInProgress = false;
    }
  }

  // ✅ MÉTHODE OPTIMISÉE : Vérifier si un élément existe déjà en base
  async checkIfExistsInDatabase(item) {
    try {
      const { type, data, operation } = item;
      
      // Pour les créations, utiliser les endpoints optimisés
      if (operation === 'create') {
        switch (type) {
          case 'patients':
            // ✅ OPTIMISÉ : Un seul appel API au lieu de récupérer tous les patients
            const patientCheck = await api.post('/patients/check-exists', {
              firstName: data.firstName,
              lastName: data.lastName,
              dateOfBirth: data.dateOfBirth
            });
            return patientCheck.data.exists;
            
          case 'consultations':
            // ✅ OPTIMISÉ : Un seul appel API au lieu de récupérer toutes les consultations
            const consultationCheck = await api.post('/consultations/check-exists', {
              patient_id: data.patient_id,
              appointment_date: data.appointment_date,
              consultation_type: data.consultation_type
            });
            return consultationCheck.data.exists;
            
          case 'vitals':
            // Pour l'instant, garder l'ancienne méthode (à optimiser plus tard)
            try {
              await api.get(`/vitals/check-exists`, {
                params: {
                  patient_id: data.patient_id,
                  recorded_date: data.recorded_date,
                  vital_type: data.vital_type
                }
              });
              return true;
            } catch (error) {
              return false;
            }
            
          case 'treatments':
            // Pour l'instant, garder l'ancienne méthode (à optimiser plus tard)
            try {
              await api.get(`/treatments/check-exists`, {
                params: {
                  patient_id: data.patient_id,
                  medication_name: data.medication_name,
                  start_date: data.start_date
                }
              });
              return true;
            } catch (error) {
              return false;
            }
            
          case 'medicalHistory':
            // Pour l'instant, garder l'ancienne méthode (à optimiser plus tard)
            try {
              await api.get(`/medical-history/check-exists`, {
                params: {
                  patient_id: data.patient_id,
                  category: data.category,
                  start_date: data.start_date
                }
              });
              return true;
            } catch (error) {
              return false;
            }
            
          default:
            return false;
        }
      }
      
      // Pour les mises à jour et suppressions, l'élément doit exister
      if (operation === 'update' || operation === 'delete') {
        try {
          await api.get(`/${type}/${data.id}`);
          return true; // L'élément existe
        } catch (error) {
          return false; // L'élément n'existe pas
        }
      }
      
      return false;
    } catch (error) {
      console.error('Erreur lors de la vérification d\'existence:', error);
      return false; // En cas d'erreur, considérer que l'élément n'existe pas
    }
  }

  async processBatch(batch) {
    const promises = batch.map(item => this.syncItem(item));
    await Promise.allSettled(promises);
  }

  // ✅ MÉTHODE SIMPLIFIÉE : Synchroniser un seul élément
  async syncItem(item) {
    try {
      let result;
      
      switch (item.type) {
        case 'patients':
          result = await this.syncPatient(item);
          break;
        case 'consultations':
          result = await this.syncConsultation(item);
          break;
        case 'vitals':
          result = await this.syncVitals(item);
          break;
        case 'treatments':
          result = await this.syncTreatment(item);
          break;
        case 'medicalHistory':
          result = await this.syncMedicalHistory(item);
          break;
        default:
          throw new Error(`Type d'élément non supporté: ${item.type}`);
      }

      // Retourner un résultat simple
      return {
        success: true,
        data: result.data || result
      };

    } catch (error) {
      console.error(`Erreur lors de la synchronisation de ${item.type}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Méthode pour mettre à jour les IDs locaux et éviter les duplications
  async updateLocalEntityId(entityType, tempId, serverId) {
    try {
      // Récupérer l'entité avec l'ID temporaire
      const entityData = await localStorageService.getEntity(entityType, tempId);
      if (entityData) {
        // Supprimer l'ancien enregistrement avec l'ID temporaire
        await localStorageService.deleteEntity(entityType, tempId);
        
        // Créer un nouvel enregistrement avec l'ID du serveur (sans isLocal)
        const updatedEntity = { 
          ...entityData, 
          id: serverId, 
          syncedAt: new Date().toISOString()
        };
        
        // Ne pas sauvegarder localement car la donnée existe maintenant sur le serveur
        // La donnée sera rechargée depuis le serveur lors du prochain chargement
        console.log(`ID mis à jour: ${tempId} → ${serverId} pour ${entityType} - Donnée supprimée du local`);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'ID local:', error);
    }
  }

  async syncPatient(item) {
    try {
      let result;
      
      if (item.operation === 'create') {
        result = await api.post('/patients', item.data);
      } else if (item.operation === 'update') {
        result = await api.put(`/patients/${item.data.id}`, item.data);
      } else if (item.operation === 'delete') {
        result = await api.delete(`/patients/${item.data.id}`);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async syncConsultation(item) {
    try {
      let result;
      
      if (item.operation === 'create') {
        result = await api.post('/consultations', item.data);
      } else if (item.operation === 'update') {
        result = await api.put(`/consultations/${item.data.id}`, item.data);
      } else if (item.operation === 'delete') {
        result = await api.delete(`/consultations/${item.data.id}`);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async syncVitals(item) {
    try {
      let result;
      
      if (item.operation === 'create') {
        result = await api.post('/vitals', item.data);
      } else if (item.operation === 'update') {
        result = await api.put(`/vitals/${item.data.id}`, item.data);
      } else if (item.operation === 'delete') {
        result = await api.delete(`/vitals/${item.data.id}`);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async syncTreatment(item) {
    try {
      let result;
      
      if (item.operation === 'create') {
        result = await api.post('/treatments', item.data);
      } else if (item.operation === 'update') {
        result = await api.put(`/treatments/${item.data.id}`, item.data);
      } else if (item.operation === 'delete') {
        result = await api.delete(`/treatments/${item.data.id}`);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async syncMedicalHistory(item) {
    try {
      let result;
      
      if (item.operation === 'create') {
        result = await api.post('/medical-history', item.data);
      } else if (item.operation === 'update') {
        result = await api.put(`/medical-history/${item.data.id}`, item.data);
      } else if (item.operation === 'delete') {
        result = await api.delete(`/medical-history/${item.data.id}`);
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async updateSyncStatus(status) {
    await localStorageService.updateSyncStatus(status);
    
    // Émettre un événement personnalisé pour notifier les composants
    window.dispatchEvent(new CustomEvent('syncStatusChanged', { detail: status }));
  }

  async getSyncStatus() {
    return await localStorageService.getSyncStatus();
  }

  async getPendingItemsCount() {
    const queue = await localStorageService.getSyncQueue();
    return queue.filter(item => item.status === 'pending').length;
  }

  async forceSync() {
    if (this.syncInProgress) {
      console.log('Synchronisation déjà en cours');
      return;
    }
    
    console.log('Synchronisation forcée');
    await this.syncPendingChanges();
  }


  async clearAllData() {
    await localStorageService.clearAllData();
  }

  // Méthode pour arrêter le service
  destroy() {
    this.stopPeriodicSync();
    window.removeEventListener('online', this.onConnectionRestored);
    window.removeEventListener('offline', this.onConnectionLost);
  }
}

// Instance singleton
const syncService = new SyncService();
export default syncService;
